<template>
    <header class="sticky top-0 z-30 bg-[var(--color-header-bg)] backdrop-blur-[var(--backdrop-blur-header)]">
        <div>
            <div class="container">
                <div class="flex items-center justify-between gap-4 py-4">
                    <!-- Logo -->
                    <CLogo class="shrink-0 text-gray-900 dark:text-white" />

                    <!-- Search - Desktop Only -->
                    <div class="hidden flex-1 md:flex md:justify-center">
                        <CSearchAutocomplete />
                    </div>

                    <!-- Right Side: Actions + User Profile -->
                    <div class="flex shrink-0 items-center gap-2">
                        <CDarkModeToggle variant="cycle" />
                        <ClientOnly>
                            <!-- Logged In State -->
                            <template v-if="isLoggedIn">
                                <NuxtLink
                                    to="/podpor-nas"
                                    no-prefetch
                                    class="hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-pink-600 dark:text-pink-300 transition-colors hover:bg-pink-50 dark:hover:bg-pink-950/40 md:inline-flex"
                                >
                                    <CIcon name="heart" class="size-4" />
                                    Podporiť
                                </NuxtLink>
                                <CreateNewWidget class="hidden md:block" />
                                <NotificationsWidget class="hidden md:block" />
                                <UserWidgetWithCompletion class="hidden md:block" />
                            </template>

                            <!-- Logged Out State -->
                            <template v-else>
                                <NuxtLink
                                    to="/podpor-nas"
                                    no-prefetch
                                    class="hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-pink-600 dark:text-pink-300 transition-colors hover:bg-pink-50 dark:hover:bg-pink-950/40 md:inline-flex"
                                >
                                    <CIcon name="heart" class="size-4" />
                                    Podporiť
                                </NuxtLink>
                                <button
                                    class="hidden rounded-full border border-default bg-surface-card px-4 py-2 text-sm font-semibold text-txt-headings transition-colors hover:bg-subtle md:flex"
                                    @click="handleLoginClick"
                                >
                                    Prihlásiť sa
                                </button>
                                <button
                                    class="hidden rounded-full bg-gray-900 dark:bg-white px-4 py-2 text-sm font-semibold text-white dark:text-gray-900 transition-colors hover:bg-gray-750 dark:hover:bg-gray-100 md:flex"
                                    @click="handleRegisterClick"
                                >
                                    Registrovať
                                </button>
                            </template>
                        </ClientOnly>

                        <!-- Mobile Menu Button -->
                        <button
                            class="flex size-10 items-center justify-center rounded-full p-2.5 transition-colors hover:bg-gray-200 dark:hover:bg-surface-card md:hidden"
                            :aria-expanded="showMobileMenu"
                            aria-label="Navigácia"
                            @click="toggleMobileMenu"
                        >
                            <svg
                                v-if="!showMobileMenu"
                                class="size-5"
                                fill="none"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3.125 5h13.75M3.125 10h13.75M3.125 15h13.75"
                                    stroke="currentColor"
                                    class="text-gray-600 dark:text-txt-body"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <svg
                                v-else
                                class="size-5"
                                fill="none"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M15 5 5 15M5 5l10 10"
                                    stroke="currentColor"
                                    class="text-gray-600 dark:text-txt-body"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tab Navigation Row (hidden when mobile menu open) -->
        <MainNavTabs v-if="!showMobileMenu" :in-header="true" />

        <!-- Mobile Menu -->
        <div v-if="showMobileMenu" class="bg-surface-card py-4 md:hidden">
            <div class="container">
                <!-- User Profile Section (if logged in) -->
                <ClientOnly>
                    <div v-if="isLoggedIn" class="mb-4 pb-4 border-b border-light">
                        <UserWidgetWithCompletion />
                    </div>
                </ClientOnly>

                <!-- Navigation Grid -->
                <div class="grid grid-cols-2 gap-2 mb-4 pb-4 border-b border-light">
                    <NuxtLink
                        v-for="item in baseNavItems"
                        :key="item.to"
                        :to="item.to"
                        no-prefetch
                        class="flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all"
                        :style="{
                            backgroundColor: route.path.startsWith(item.to) ? item.bgColor : 'transparent',
                            color: route.path.startsWith(item.to) ? navColor(item) : undefined
                        }"
                        :class="[
                            !route.path.startsWith(item.to) &&
                                'text-gray-700 dark:text-txt-body hover:bg-gray-100 dark:hover:bg-surface-card'
                        ]"
                        @click="closeMobileMenu"
                    >
                        <CIcon :name="item.icon" class="size-5" :style="{ color: navColor(item) }" />
                        <span class="text-sm font-semibold">{{ item.label }}</span>
                    </NuxtLink>
                </div>

                <!-- Action Buttons -->
                <div class="flex flex-col gap-2 pt-2 border-t border-light">
                    <NuxtLink
                        to="/podpor-nas"
                        no-prefetch
                        class="flex w-full items-center justify-center gap-1.5 rounded-full border border-pink-200 dark:border-pink-900/60 px-4 py-2 text-sm font-semibold text-pink-600 dark:text-pink-300 transition-colors hover:bg-pink-50 dark:hover:bg-pink-950/40"
                        @click="closeMobileMenu"
                    >
                        <CIcon name="heart" class="size-4" />
                        Podporiť
                    </NuxtLink>
                    <ClientOnly>
                        <template v-if="isLoggedIn">
                            <CreateNewWidget />
                            <NotificationsWidget />
                        </template>
                        <template v-else>
                            <button
                                class="w-full rounded-full bg-gray-900 dark:bg-white px-4 py-2 text-sm font-semibold text-white dark:text-gray-900 transition-colors hover:bg-gray-750 dark:hover:bg-gray-100"
                                @click="handleMobileRegisterClick"
                            >
                                Registrovať
                            </button>
                            <button
                                class="w-full rounded-full border border-default bg-surface-card px-4 py-2 text-sm font-semibold text-txt-headings transition-colors hover:bg-subtle"
                                @click="handleMobileLoginClick"
                            >
                                Prihlásiť sa
                            </button>
                        </template>
                    </ClientOnly>
                </div>
            </div>
        </div>
    </header>
</template>

<script setup lang="ts">
import { useApplicationStore } from '@/stores/application'
import CSearchAutocomplete from '@/components/ui/CSearchAutocomplete.vue'
import MainNavTabs from '@/components/navigation/MainNavTabs.vue'

// Signed-in only widgets: loaded on demand so anonymous visitors don't download them
// (they pull in notifications, communities and profile-completion code).
const CreateNewWidget = defineAsyncComponent(() => import('@/components/common/CreateNewWidget.vue'))
const NotificationsWidget = defineAsyncComponent(() => import('@/components/common/NotificationsWidget.vue'))
const UserWidgetWithCompletion = defineAsyncComponent(() => import('@/components/common/UserWidgetWithCompletion.vue'))

const { isDark } = useDarkMode()
const applicationStore = useApplicationStore()
const showMobileMenu = ref(false)
const authModal = useAuthModal()

const isLoggedIn = computed(() => applicationStore.isLoggedIn)
const route = useRoute()

const baseNavItems = [
    {
        to: '/sebapoznanie',
        label: 'Sebapoznanie',
        icon: 'heart-outline',
        color: '#ec4899',
        bgColor: 'rgba(236,72,153,0.1)'
    },
    { to: '/mentori', label: 'Mentoring', icon: 'mentoring', color: '#047274', bgColor: 'rgba(4,114,116,0.1)' },
    {
        to: '/dobrobaza',
        label: 'Dobrovoľníctvo',
        icon: 'volunteering',
        color: '#eab308',
        bgColor: 'rgba(234,179,8,0.1)'
    },
    { to: '/praca', label: 'Práca', icon: 'jobs', color: '#f97316', bgColor: 'rgba(249,115,22,0.1)' },
    { to: '/vzdelavanie', label: 'Vzdelávanie', icon: 'skills', color: '#c24cfc', bgColor: 'rgba(194,76,252,0.1)' },
    {
        to: '/pre-organizacie',
        label: 'Pre organizácie',
        icon: 'building',
        color: '#f97316',
        bgColor: 'rgba(249,115,22,0.1)'
    },
    // Krúžky sú zatiaľ IBA v mobilnom menu. Desktopový pás záložiek je samostatný
    // komponent (MainNavTabs.vue) s vlastným natvrdo písaným zoznamom — tento sem
    // nesiaha, takže pridanie položky desktop neovplyvní.
    { to: '/kruzky', label: 'Krúžky', icon: 'group', color: '#40b153', bgColor: 'rgba(64,177,83,0.1)' }
]

function navColor(item: { to: string; color: string }) {
    return isDark.value && item.to === '/mentori' ? '#4AD8DC' : item.color
}

function toggleMobileMenu() {
    showMobileMenu.value = !showMobileMenu.value
}

function closeMobileMenu() {
    showMobileMenu.value = false
}

function handleLoginClick() {
    authModal.openLogin()
}

function handleRegisterClick() {
    authModal.openRegister()
}

function handleMobileLoginClick() {
    handleLoginClick()
    closeMobileMenu()
}

function handleMobileRegisterClick() {
    handleRegisterClick()
    closeMobileMenu()
}
</script>

<style scoped>
/* Hide scrollbar but keep functionality */
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
</style>
